import { z } from '../zod';
import { DashboardUrlField } from '../shared/dashboardUrl';
import { DiscountValidationSchema, BaseChargeResponseValidationSchema } from '../charge/';
import { PaginatedListSchema } from '../shared/paginatedList';
import { AttendeeDetailsResponseShape } from '../group-registration/attendeeResponse';
import { beta } from '../beta';

export const TicketSchema = z.object({
  name: z.string().nullish().meta({
    description: 'Ticket name.',
  }),
  ticketTypeId: z.string().nullish().meta({
    description: 'Ticket type ID.',
  }),
  capacity: z.number().nullish().meta({
    description: 'Ticket type capacity. If not set ticket capacity is unlimited.',
  }),
  reference: z.string().nullish().meta({
    description: 'Ticket reference.',
  }),
  ticketGroupId: z.string().nullish().meta({
    description: 'Ticket group ID.',
  }),
  ticketGroupName: z.string().nullish().meta({
    description: 'Ticket group name.',
  }),
  ticketGroupCapacity: z.number().nullish().meta({
    description: 'Ticket group capacity.',
  }),
  ticketGroupReference: z.string().nullish().meta({
    description: 'Ticket group reference.',
  }),
  quantity: z.number().nullish().meta({
    description: 'Quantity purchased for this ticket line item.',
  }),
  price: z.number().nullish().meta({
    description: 'Per-ticket price in the smallest currency unit.',
  }),
  originalPrice: z.number().nullish().meta({
    description: 'Per-ticket price before discount, in the smallest currency unit.',
  }),
  pricing: z.enum(['paid', 'pwyw', 'free']).nullish().meta({
    description:
      "Ticket type pricing at time of purchase. 'pwyw' means the buyer chose the amount (pay what you want).",
  }),
  fee: z
    .object({
      name: z.string().nullish().meta({ description: 'Name of the fee.' }),
      amount: z.number().nullish().meta({
        description: 'Fee per ticket at the time of purchase, in the smallest currency unit.',
      }),
      totalAmount: z.number().nullish().meta({
        description:
          'Total fee for this line item, in the smallest currency unit: `amount` × `quantity`, less the share of a ticket-scoped coupon that applied to fees. An order-level coupon is not reflected here; see `ticketFeesAmount`.',
      }),
      display: z.enum(['included', 'separate']).nullish().meta({
        description: 'How the fee was presented at checkout.',
      }),
    })
    .nullish()
    .meta({
      description:
        'The ticket type fee charged on this line item, kept separate from `price`. Null when the ticket type had no fee.',
    }),

  discount: DiscountValidationSchema.nullish(),
  // @TODO uncomment when coupon ticket types goes live
  // coupon: z
  //   .object({
  //     amount: z.number(),
  //   })
  //   .nullish(),
});

export const BookingResponseValidationSchema = BaseChargeResponseValidationSchema.extend({
  eventTitle: z.string().nullish().meta({
    description:
      'Human-readable event name. (e.g. "Future of AI Summit 2026", "Conference"). Present on all records. Use this instead of `pageSlug` when presenting event information. Searchable via `search`',
  }),
  tickets: z.array(TicketSchema).nullish().meta({
    description: 'Array of ticket objects.',
  }),
  attendeeStatus: beta(
    z.enum(['complete', 'missing', 'flagged', 'optional']).optional().meta({
      description:
        "Attendee details: `complete` (every ticket has an attendee), `missing` (some tickets don't yet), `flagged` (one or more attendees flagged for the seller to review) or `optional` (the customer isn't asked, so the seller adds attendees if they want to). Absent while a booking has no attendee details to track.",
    }),
  ),
  attendees: beta(
    z
      .array(
        z.object({
          position: z.number().meta({
            description:
              'Attendee number, 1 for the first ticket, in the order the event lists its ticket types.',
          }),
          ticketTypeId: z
            .string()
            .meta({ description: 'ID of the ticket type this attendee holds.' }),
          ticketId: z.string().optional().meta({
            description:
              "ID of the attendee's ticket. Absent until the booking's tickets are created, which happens once it's paid or confirmed.",
          }),
          ...AttendeeDetailsResponseShape,
        }),
      )
      .optional()
      .meta({
        description:
          'The attendees added so far, one per ticket, on events that collect attendee details. A ticket without an attendee has no entry.',
      }),
  ),
  ticketFeesAmount: z.number().nullish().meta({
    description:
      'Booking fees actually charged on this booking, in the smallest currency unit. Starts from the sum of `tickets[].fee.totalAmount`; an order-level coupon that applies to fees reaches them only after it has used up the ticket prices. 0 on a complimentary booking or when no ticket had a fee.',
  }),
}).openapi('Booking', { description: '' });

export const GetBookingsResponseValidationSchema = PaginatedListSchema(
  BookingResponseValidationSchema,
);

export const BookingDetailResponseValidationSchema = z
  .object({
    data: BookingResponseValidationSchema,
  })
  .meta({ id: 'BookingDetailResponse', description: 'Envelope containing one booking.' });

export const BookingToolDataValidationSchema = BookingResponseValidationSchema.extend({
  dashboardUrl: DashboardUrlField,
});

export const BookingDetailToolResponseValidationSchema = z.object({
  data: BookingToolDataValidationSchema,
});

export const BookingsToolResponseValidationSchema = PaginatedListSchema(
  BookingToolDataValidationSchema,
);
